/**
 * AcceptInvite — public page at /invite/:token.
 * Resolves a share invitation and lets the recipient join:
 *  - logged-in user → one-click accept
 *  - no account yet (view/feedback) → create a free password-only account
 *  - account exists → prompt to log in first
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, setBearer, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eye, MessageSquare, PenLine, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_COPY = {
  viewer: { label: "View only", line: "You'll be able to read this screenplay.", Icon: Eye },
  feedback: { label: "Feedback", line: "You'll be able to read it and leave notes for the writer.", Icon: MessageSquare },
  cowriter: { label: "Co-writer", line: "You'll have full edit access (co-writing needs your own subscription).", Icon: PenLine },
};

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(undefined); // undefined=loading, null=invalid
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = useCallback(async () => {
    try { const { data } = await api.get(`/shares/lookup/${token}`); setInfo(data); }
    catch { setInfo(null); }
  }, [token]);
  useEffect(() => { lookup(); }, [lookup]);

  const accept = async (withCreds) => {
    setBusy(true);
    try {
      const payload = withCreds ? { password, name } : {};
      const { data } = await api.post(`/shares/accept/${token}`, payload);
      if (data.access_token) setBearer(data.access_token);
      await refresh();
      toast.success("You're in!");
      navigate(`/app/scripts/${data.script_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't accept the invite");
    } finally { setBusy(false); }
  };

  if (info === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--canvas)]">
        <Loader2 className="animate-spin text-[var(--ink-muted)]" data-testid="invite-loading" />
      </div>
    );
  }

  if (info === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--canvas)] p-6">
        <div className="text-center max-w-md" data-testid="invite-invalid">
          <h1 className="font-display text-3xl text-[var(--ink-primary)]">This invite isn't valid</h1>
          <p className="text-[var(--ink-secondary)] mt-3">It may have been revoked or expired. Ask the writer to send you a new link.</p>
          <Link to="/" className="btn-brand inline-block mt-6 px-5 py-2.5">Back home</Link>
        </div>
      </div>
    );
  }

  const roleMeta = ROLE_COPY[info.role] || ROLE_COPY.viewer;
  const RoleIcon = roleMeta.Icon;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--canvas)] p-6">
      <div className="w-full max-w-md bg-[var(--paper)] border border-[var(--line)] rounded-xl shadow-xl overflow-hidden" data-testid="invite-card">
        <div className="h-1.5 w-full bg-[var(--brand)]" />
        <div className="p-8">
          <div className="flex items-center gap-2 text-[var(--brand)] mb-4">
            <RoleIcon size={18} />
            <span className="text-xs uppercase tracking-wider font-medium">{roleMeta.label} access</span>
          </div>
          <h1 className="font-display text-2xl text-[var(--ink-primary)] leading-snug">
            {info.owner_name} invited you to <span className="text-[var(--brand)]">{info.script_title}</span>
          </h1>
          <p className="text-sm text-[var(--ink-secondary)] mt-3">{roleMeta.line}</p>

          {info.already_member ? (
            <button onClick={() => accept(false)} disabled={busy} className="btn-brand w-full py-3 mt-6" data-testid="invite-open-btn">
              Open the script
            </button>
          ) : info.logged_in ? (
            <>
              <p className="text-xs text-[var(--ink-muted)] mt-4">Signed in as {info.logged_in_email}</p>
              <button onClick={() => accept(false)} disabled={busy} className="btn-brand w-full py-3 mt-3" data-testid="invite-accept-btn">
                {busy ? "Joining…" : "Accept invitation"}
              </button>
            </>
          ) : info.account_exists ? (
            <div className="mt-6" data-testid="invite-login-prompt">
              <p className="text-sm text-[var(--ink-secondary)]">You already have an account for <strong>{info.invited_email}</strong>. Log in to accept.</p>
              <Link to={`/login?next=/invite/${token}`} className="btn-brand inline-block w-full text-center py-3 mt-3" data-testid="invite-login-link">
                Log in to accept
              </Link>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); accept(true); }} className="mt-6 space-y-3" data-testid="invite-signup-form">
              <div className="text-xs text-[var(--ink-muted)]">Create your free account for <strong>{info.invited_email}</strong>{info.role !== "cowriter" ? " — no card, ever." : "."}</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="invite-name-input"
                className="w-full px-3 py-2.5 bg-white border border-[var(--line)] rounded-md text-sm focus:border-[var(--brand)] outline-none"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a password (8+ characters)"
                required
                minLength={8}
                data-testid="invite-password-input"
                className="w-full px-3 py-2.5 bg-white border border-[var(--line)] rounded-md text-sm focus:border-[var(--brand)] outline-none"
              />
              <button type="submit" disabled={busy || password.length < 8} className="btn-brand w-full py-3 disabled:opacity-60" data-testid="invite-create-btn">
                {busy ? "Creating account…" : "Create account & open script"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
